import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import Preloader from './Preloader/Preloader';
import LogoLoader from './Common/LogoLoader';

const SystemStatusDashboard = ({ compact = false, showRefresh = true }) => {
  const [healthData, setHealthData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  const { token } = useAuth();
  const { isDarkMode } = useTheme();

  const fetchHealthData = useCallback(async () => {
    setLoading(true);
    try {
      const headers = {};
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }
      
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/health`, {
        headers
      });
      const data = await response.json();
      setHealthData(data);
      setLastUpdated(new Date().toLocaleString());
    } catch (error) {
      setHealthData({
        status: "ERROR",
        error: "Failed to connect to backend",
        services: {
          server: { status: "Unreachable" },
          database: { status: "Unknown" }
        }
      });
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchHealthData();
    
    // Auto-refresh every 30 seconds only if not compact
    if (!compact) {
      const interval = setInterval(fetchHealthData, 30000);
      return () => clearInterval(interval);
    }
  }, [compact, fetchHealthData]);

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'connected':
      case 'running':
      case 'ok':
        return 'bg-green-600 text-white';
      case 'disconnected':
      case 'connection failed':
      case 'connection error':
      case 'unreachable':
        return 'bg-red-600 text-white';
      case 'unknown':
      case 'checking...':
        return 'bg-yellow-600 text-white';
      default:
        return 'bg-gray-600 text-white';
    }
  };

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case 'connected':
      case 'running':
      case 'ok':
        return '✅';
      case 'disconnected':
      case 'connection failed':
      case 'connection error':
      case 'unreachable':
        return '❌';
      case 'unknown':
      case 'checking...':
        return '⚠️';
      default:
        return '❓';
    }
  };

  if (compact) {
    return (
      <div className={`rounded-lg p-4 ${
        isDarkMode ? 'bg-gray-800/50' : 'bg-white/70 border border-gray-200'
      }`}>
        <h3 className={`text-lg font-semibold mb-3 flex items-center ${
          isDarkMode ? 'text-gray-200' : 'text-gray-800'
        }`}>
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          System Status
        </h3>
        
        {healthData ? (
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <span className={`text-sm ${
                isDarkMode ? 'text-gray-400' : 'text-gray-600'
              }`}>Backend:</span>
              <span className={`px-2 py-1 rounded text-xs ${getStatusColor(healthData.services?.server?.status)}`}>
                {getStatusIcon(healthData.services?.server?.status)} {healthData.services?.server?.status || 'Unknown'}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className={`text-sm ${
                isDarkMode ? 'text-gray-400' : 'text-gray-600'
              }`}>Database:</span>
              <span className={`px-2 py-1 rounded text-xs ${getStatusColor(healthData.services?.database?.status)}`}>
                {getStatusIcon(healthData.services?.database?.status)} {healthData.services?.database?.status || 'Unknown'}
              </span>
            </div>
            {lastUpdated && (
              <p className={`text-xs mt-2 ${
                isDarkMode ? 'text-gray-500' : 'text-gray-400'
              }`}>
                Updated: {lastUpdated}
              </p>
            )}
          </div>
        ) : (
          <div className="text-center py-4">
            <LogoLoader size={60} />
          </div>
        )}
      </div>
    );
  }

  return (
    <div>
      {lastUpdated && showRefresh && (
        <div className="text-center mb-6">
          <p className="text-sm text-gray-400">
            Last updated: {lastUpdated}
          </p>
        </div>
      )}

      {/* Refresh Button */}
      {showRefresh && (
        <div className="text-center mb-8">
          <button
            onClick={fetchHealthData}
            disabled={loading}
            className="inline-flex items-center bg-blue-600 hover:bg-blue-700 disabled:bg-blue-800 disabled:cursor-not-allowed transition-all duration-200 px-6 py-3 rounded-lg font-medium text-white transform hover:scale-105"
          >
            {loading ? (
              <>
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Checking...
              </>
            ) : (
              <>
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Refresh Status
              </>
            )}
          </button>
        </div>
      )}

      {/* System Status */}
      {healthData ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          {/* Backend Status */}
          <div className="bg-card dark:bg-dark-card rounded-2xl shadow-card p-8 animate-fade-in transition-all">
            <h3 className="text-2xl font-bold mb-4 text-primary-500 flex items-center justify-center">
              <svg className="w-6 h-6 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2m-2-4h.01M17 16h.01" />
              </svg>
              Backend Server
            </h3>
            
            <div className="space-y-4">
              <div className="bg-gray-800/50 rounded-lg p-4">
                <div className="flex justify-between items-center">
                  <span className="text-gray-300 font-medium">Status:</span>
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(healthData.services?.server?.status)}`}>
                    {getStatusIcon(healthData.services?.server?.status)} {healthData.services?.server?.status || 'Unknown'}
                  </span>
                </div>
              </div>
              
              <div className="bg-gray-800/50 rounded-lg p-4">
                <div className="flex justify-between items-center">
                  <span className="text-gray-300 font-medium">Response Time:</span>
                  <span className="text-gray-100 font-mono">
                    {healthData.services?.server?.responseTime || 'N/A'}
                  </span>
                </div>
              </div>
              
              {healthData.services?.server?.error && (
                <div className="bg-red-900/30 border border-red-500/50 rounded-lg p-4">
                  <span className="text-red-300 text-sm">
                    Error: {healthData.services.server.error}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Database Status */}
          <div className="bg-card dark:bg-dark-card rounded-2xl shadow-card p-8 animate-fade-in transition-all">
            <h3 className="text-2xl font-bold mb-4 text-secondary-500 flex items-center justify-center">
              <svg className="w-6 h-6 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4" />
              </svg>
              Database (MongoDB)
            </h3>
            
            <div className="space-y-4">
              <div className="bg-gray-800/50 rounded-lg p-4">
                <div className="flex justify-between items-center">
                  <span className="text-gray-300 font-medium">Status:</span>
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(healthData.services?.database?.status)}`}>
                    {getStatusIcon(healthData.services?.database?.status)} {healthData.services?.database?.status || 'Unknown'}
                  </span>
                </div>
              </div>
              
              <div className="bg-gray-800/50 rounded-lg p-4">
                <div className="flex flex-col gap-2">
                  <span className="text-gray-300 font-medium">MongoDB URI:</span>
                  <span className="text-gray-100 font-mono text-sm break-all bg-gray-700/50 px-2 py-1 rounded">
                    {healthData.services?.database?.uri || 'Not configured'}
                  </span>
                </div>
              </div>
              
              {healthData.services?.database?.error && (
                <div className="bg-red-900/30 border border-red-500/50 rounded-lg p-4">
                  <span className="text-red-300 text-sm">
                    Error: {healthData.services.database.error}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      ) : (
        <div className="text-center py-12">
          <Preloader 
            size="medium" 
            message="Loading system status..." 
          />
        </div>
      )}

      {/* App Information */}
      <div className="glass-effect rounded-xl shadow-2xl p-8 mb-8">
        <h3 className="text-2xl font-semibold mb-6 text-center text-purple-400 flex items-center justify-center">
          <svg className="w-6 h-6 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          Application Information
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-gray-800/50 rounded-lg p-4 hover:bg-gray-800/70 transition-colors">
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-400 mb-1">
                {process.env.REACT_APP_NAME || 'Vitria'}
              </div>
              <div className="text-gray-400 text-sm">Application Name</div>
            </div>
          </div>
          
          <div className="bg-gray-800/50 rounded-lg p-4 hover:bg-gray-800/70 transition-colors">
            <div className="text-center">
              <div className="text-2xl font-bold text-green-400 mb-1">
                {process.env.REACT_APP_VERSION || '1.0.0'}
              </div>
              <div className="text-gray-400 text-sm">Version</div>
            </div>
          </div>
          
          <div className="bg-gray-800/50 rounded-lg p-4 hover:bg-gray-800/70 transition-colors">
            <div className="text-center">
              <div className="text-2xl font-bold text-purple-400 mb-1">
                {process.env.REACT_APP_ENV || 'development'}
              </div>
              <div className="text-gray-400 text-sm">Environment</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SystemStatusDashboard;
